import { LEGACY_UTC_OFFSETS, SELECTABLE_UTC_OFFSETS } from '@navstation-portal/shared/data-model/time';

/** Every offset NSP knows. A user can select some of them. The others stay readable. */
const KNOWN_OFFSETS: readonly number[] = [...SELECTABLE_UTC_OFFSETS, ...LEGACY_UTC_OFFSETS];

const TVA_TIMEZONE_PREFIX = 'TVA/GMT';

/** The cloud API keys offset zero `UTC`, not `TVA/GMT+0`. It is the only irregular key. */
const UTC_TIMEZONE_KEY = 'UTC';

const MINUTES_PER_HOUR = 60;

/** Gives the `(UTC±HH:MM)` label for an offset. The label comes from the offset, so the two agree. */
export function formatUtcOffset(offset: number): string {
  const sign = offset < 0 ? '-' : '+';
  const absolute = Math.abs(offset);
  const hours = Math.floor(absolute);
  // Rounded, not truncated. An offset that arithmetic produced can be inexact.
  const minutes = Math.round((absolute - hours) * MINUTES_PER_HOUR);

  return `(UTC${sign}${pad(hours)}:${pad(minutes)})`;
}

/**
 * Gives the key the cloud API carries an offset under, or nothing.
 *
 * It covers the retired offsets. A report saved against one keeps that offset
 * when a user edits a different field.
 */
export function utcOffsetToTvaTimezone(offset: number | undefined): string | undefined {
  if (offset == null || !KNOWN_OFFSETS.includes(offset)) return undefined;
  if (offset === 0) return UTC_TIMEZONE_KEY;

  return `${TVA_TIMEZONE_PREFIX}${offset > 0 ? '+' : ''}${offset}`;
}

/** Gives the offset a cloud API key names, or nothing if NSP does not know the key. */
export function tvaTimezoneToUtcOffset(key: string): number | undefined {
  if (key === UTC_TIMEZONE_KEY) return 0;
  if (!key.startsWith(TVA_TIMEZONE_PREFIX)) return undefined;

  // `Number('')` is zero. Without this test, a bare prefix reads back as UTC.
  const signedOffset = key.slice(TVA_TIMEZONE_PREFIX.length);
  if (signedOffset === '') return undefined;

  return knownOffset(Number(signedOffset));
}

/**
 * Gives the offset behind a value the cloud API sent, or nothing.
 *
 * It accepts a number, a numeric string and a `TVA/GMT±N` key. The offshore
 * response declares one type and holds a different value at run time. A
 * developer machine cannot reach the API to find which one is correct. All
 * three readings are therefore accepted.
 *
 * The function is total. An input it cannot read gives nothing, and it never
 * throws.
 */
export function resolveUtcOffset(value: unknown): number | undefined {
  if (typeof value === 'number') return knownOffset(value);
  if (typeof value !== 'string' || value === '') return undefined;

  return tvaTimezoneToUtcOffset(value) ?? knownOffset(Number(value));
}

/** Tells you if a user can select this offset. A retired offset is readable, but not selectable. */
export function isSelectableUtcOffset(offset: number): boolean {
  return SELECTABLE_UTC_OFFSETS.includes(offset);
}

function knownOffset(offset: number): number | undefined {
  return KNOWN_OFFSETS.includes(offset) ? offset : undefined;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
